import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { DesignContextAvailabilityService } from './design-context-availability.service';
import { DrawingsAvailabilityService } from './drawings-availability.service';
import { LocationPickerComponent } from '../components/location-picker/location-picker.component';
import { DESIGN_CONTEXT_PICKER_DIALOG } from '../components/design-context-picker/design-context-picker.token';
// Type-only: the dialog class itself comes through DESIGN_CONTEXT_PICKER_DIALOG (it imports @arcgis/core).
import type { DesignContextPickerData, PickerFeatureLauncher } from '../components/design-context-picker/design-context-picker-dialog.component';
import { MapFeatureLink } from '../models/map-feature-link';
import type { FeatureSource } from '../models/control-form';
import { projectDetails } from '../../modules/projects/core/projects.selectors';

export interface LocationPickerLaunch {
  /** Default: `projectDetails` from the store. */
  projectId?: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  location?: string | null;
  locationText?: string | null;
  enableLocationSection?: boolean;
  showIncludeGpsLocationCheckbox?: boolean;
  /** The record's current link, when the caller knows it. Omit it and the picker leaves the link untouched unless the user links. */
  existingLink?: MapFeatureLink | null;
  /** "issue", "bid item", …; default "record". */
  recordLabel?: string;
  /** 7.2: the record's markup starts as this kind (As-built for an issue in an as-built category); default note */
  markupKind?: 'note' | 'as_built' | null;
  /** The record's station in feet, when known: the picker's Drawing tab lists sheets covering it first. */
  station?: number | null;
  /**
   * False when the record receiving the pick can't hold a design-context link (e.g. item postings):
   * the Google picker opens without a catalog check, so no link is offered and silently dropped.
   */
  designContext?: boolean;
  /** W49: "Mark on drawing" opens the picker on its Drawing tab; otherwise it opens on Map */
  initialTab?: 'map' | 'drawing';
  /**
   * The form's `ui_config.feature_source` (form header Location only): the ArcGIS picker may restrict itself to that
   * layer and returns the linked feature's properties for the prefill. Ignored by the Google picker.
   */
  featureSource?: FeatureSource | null;
  /** A launcher form's map-first pick (see PickerFeatureLauncher). Needs the ArcGIS picker: see `designContextOnly`. */
  featureLauncher?: PickerFeatureLauncher | null;
  /**
   * Only the ArcGIS picker will do (a launcher form): without a published catalog (or offline, or on a timeout) nothing
   * opens and `open()` resolves null, instead of falling back to the Google picker.
   */
  designContextOnly?: boolean;
}

/**
 * A slow manifest answer must not hold the pin click: past this, the Google picker opens.
 * Only a 404 is cached, so on a flaky network each click can pay this wait again — keep it short.
 */
export const AVAILABILITY_TIMEOUT_MS = 1500;

/**
 * The one door to the Location pin. Projects with a published map catalog get
 * the ArcGIS picker (design layers, feature linking); everyone else — and any
 * project whose catalog check fails, times out or runs offline — keeps the
 * Google picker unchanged. Both publish on `dailyReportService.selectedLocation`.
 * Only one picker is ever opening or open: a second call resolves to null.
 * While the catalog check runs, `checkingCatalog()` is true and the page shows a progress cursor.
 */
@Injectable({ providedIn: 'root' })
export class LocationPickerLauncherService {
  private dialog = inject(MatDialog);
  private availability = inject(DesignContextAvailabilityService);
  private drawingsAvailability = inject(DrawingsAvailabilityService);
  private store = inject(Store);
  private arcgisDialog = inject(DESIGN_CONTEXT_PICKER_DIALOG, { optional: true });
  private document = inject(DOCUMENT);
  private busy = false;
  /** True while the availability check holds the click (at most AVAILABILITY_TIMEOUT_MS); callers may bind it. */
  readonly checkingCatalog = signal(false);

  async open(launch: LocationPickerLaunch): Promise<MatDialogRef<unknown> | null> {
    if (this.busy) return null;
    this.busy = true;
    let ref: MatDialogRef<unknown> | null;
    try {
      ref = await this.openPicker(launch);
    } catch (error) {
      this.busy = false;
      throw error;
    }
    const closed = ref?.afterClosed?.();
    if (!closed) {
      this.busy = false;
    } else {
      const release = () => { this.busy = false; };
      closed.pipe(take(1)).subscribe({ next: release, complete: release, error: release });
    }
    return ref;
  }

  private async openPicker(launch: LocationPickerLaunch): Promise<MatDialogRef<unknown> | null> {
    const projectId = launch.projectId || (await firstValueFrom(this.store.select(projectDetails).pipe(take(1))))?.id;
    const found = launch.designContext !== false && this.arcgisDialog && projectId ? await this.pickerTargets(projectId) : null;
    // A launcher form needs the catalog's layers: drawings alone do not open the picker for it
    if (found && (found.catalog || !launch.designContextOnly)) {
      const data: DesignContextPickerData = {
        projectId,
        latitude: launch.latitude,
        longitude: launch.longitude,
        location: launch.location ?? null,
        locationText: launch.locationText ?? launch.location ?? '',
        enableLocationSection: !!launch.enableLocationSection,
        showIncludeGpsLocationCheckbox: !!launch.showIncludeGpsLocationCheckbox,
        existingLink: launch.existingLink ?? null,
        recordLabel: launch.recordLabel || 'record',
        ...(launch.markupKind ? { markupKind: launch.markupKind } : {}),
        station: launch.station ?? null,
        hasCatalog: found.catalog,
        drawingsAvailable: found.drawings,
        ...(launch.initialTab ? { initialTab: launch.initialTab } : {}),
        ...(launch.featureSource ? { featureSource: launch.featureSource } : {}),
        ...(launch.featureLauncher ? { featureLauncher: launch.featureLauncher } : {}),
      };
      // 90vw x 90vh: the picker map fills what it can (a bigger capture); MatDialog's defaults (80vw, and the
      // theme's max-height) would squeeze it. The picker sizes itself to the same bounds.
      // panelClass: MDC draws the box on .mat-mdc-dialog-surface, which the global 8px rule (on the container) misses.
      return this.dialog.open(this.arcgisDialog, {
        disableClose: true, maxWidth: '90vw', maxHeight: '90vh', panelClass: 'design-context-picker-panel', data,
      });
    }
    if (launch.designContextOnly) return null;
    // LocationPickerComponent reads only these keys (plus height/width, which no launcher caller sets).
    return this.dialog.open(LocationPickerComponent, {
      disableClose: true,
      data: {
        latitude: launch.latitude,
        longitude: launch.longitude,
        location: launch.location ?? null,
        readOnly: false,
        enableLocationSection: !!launch.enableLocationSection,
        showIncludeGpsLocationCheckbox: !!launch.showIncludeGpsLocationCheckbox,
        locationText: launch.locationText ?? launch.location ?? '',
      },
    });
  }

  /**
   * Starts the catalog check ahead of the first pin tap (fire-and-forget), so `open()` usually
   * answers from the availability cache instead of holding the click. No-op offline or without a project.
   */
  prewarm(projectId: string): void {
    if (!projectId) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    this.availability.available(projectId).catch(() => undefined);
    this.drawingsAvailability.available(projectId).catch(() => undefined);
  }

  /**
   * What the ArcGIS picker can offer (W17). The catalog alone decides, as before: a published catalog opens it at once,
   * and the drawings answer only adds the Drawing tab, whenever it lands (`drawings` stays a promise the dialog follows;
   * slow or failed is "no tab", never "no picker"). Without a catalog, the drawings answer decides within the same
   * timeout. Neither (Google picker) when offline, on any error or when the answers take too long.
   */
  private async pickerTargets(projectId: string): Promise<{ catalog: boolean; drawings: Promise<boolean> } | null> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
    let timer: ReturnType<typeof setTimeout>;
    const deadline = new Promise<false>(resolve => { timer = setTimeout(() => resolve(false), AVAILABILITY_TIMEOUT_MS); });
    const answer = (check: () => Promise<boolean>) => Promise.resolve().then(check).then(Boolean).catch(() => false);
    // Both start now, so a no-catalog project waits at most the one timeout in total.
    const catalogCheck = answer(() => this.availability.available(projectId));
    const drawings = answer(() => this.drawingsAvailability.available(projectId));
    this.setChecking(true);
    try {
      const catalog = await Promise.race([catalogCheck, deadline]);
      if (catalog) return { catalog: true, drawings };
      const drawingsNow = await Promise.race([drawings, deadline]);
      return drawingsNow ? { catalog: false, drawings: Promise.resolve(true) } : null;
    } finally {
      clearTimeout(timer);
      this.setChecking(false);
    }
  }

  private setChecking(checking: boolean): void {
    this.checkingCatalog.set(checking);
    const body = this.document?.body;
    if (body) body.style.cursor = checking ? 'progress' : '';
  }
}
